import { useState, useEffect } from 'react';
import type { PipelineTemplate, PipelineStage } from '@recruitai/shared';
import { pipelineApi } from '@/lib/pipeline-api';
import { Button } from '@/components/ui/button';
import { FileDown, Trash2 } from 'lucide-react';

interface TemplateSelectorProps {
  onSelect: (stages: PipelineStage[]) => void;
}

export function TemplateSelector({ onSelect }: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<PipelineTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await pipelineApi.listTemplates();
      setTemplates(data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && templates.length === 0) {
      loadTemplates();
    }
  }, [open]);

  const handleDelete = async (templateId: string) => {
    try {
      await pipelineApi.deleteTemplate(templateId);
      setTemplates(prev => prev.filter(t => t.templateId !== templateId));
    } catch {
      // silently fail
    }
  };

  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        <FileDown className="h-4 w-4 mr-1" /> Carregar Template
      </Button>
    );
  }

  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Templates de Pipeline</span>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>Fechar</Button>
      </div>
      {loading && <p className="text-sm text-muted-foreground">Carregando...</p>}
      {!loading && templates.length === 0 && (
        <p className="text-sm text-muted-foreground">Nenhum template salvo</p>
      )}
      {templates.map(tpl => (
        <div key={tpl.templateId} className="flex items-center justify-between p-2 bg-muted rounded-md">
          <button
            type="button"
            className="text-sm text-left hover:underline"
            onClick={() => { onSelect(tpl.stages); setOpen(false); }}
          >
            {tpl.name} ({tpl.stages.length} etapas)
          </button>
          <Button type="button" variant="ghost" size="sm" onClick={() => handleDelete(tpl.templateId)}
            aria-label={`Excluir template ${tpl.name}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}
